import * as React from 'react'
import {
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
  ScrollRestoration,
  createBrowserRouter,
} from 'react-router-dom'
import { Authenticate } from '../auth/AuthenticateRoutes'
import { ForgotPassword } from '../auth/ForgotPassword'
import { Login } from '../auth/Login'
import { Logout } from '../auth/Logout'
import { ResetPassword } from '../auth/ResetPassword'
import { DocRoute } from '../docs/DocRoute'
import esignAgreement from '../docs/EsignConsent.html?raw'
import { FetchDocRoute } from '../docs/FetchDocRoute'
import { employeeDashboardRoutes } from '../employeeDashboard/employeeDashboardRoutes'
import { EnrollmentRouter } from '../enrollment/EnrollmentRouter'
import { FindEnrollmentCode } from '../enrollment/FindEnrollmentCode'
import { InactiveEmployer } from '../enrollment/InactiveEmployer'
import { EnrollmentLanding } from '../enrollment/landing/EnrollmentLanding'
import { NewEnrollmentSummary } from '../enrollment/NewEnrollmentSummary'
import { ReenrollmentConfirmation } from '../enrollment/reenrollment/ReenrollmentConfirmation'
import { Register } from '../enrollment/Register'
import { TrackEnrollmentCode } from '../enrollment/TrackEnrollmentCode'
import { Home } from '../home/Home'
import { orgDashboardRoutes } from '../orgDashboard/orgDashboardRoutes'
import { TrackAccountStatus } from '../telemetry/TrackAccountStatus'
import { TrackUserId } from '../telemetry/TrackUserId'
import { TrackUtmParams } from '../telemetry/TrackUtmParams'
import { ViewTracker } from '../telemetry/ViewTracker'
import { CustodialAccountAgreement } from '../terms/CustodialAccountAgreement'
import { UserWebRouteObject, normalizeRoute } from './normalizeRoutes'
import { NotFound } from './NotFound'
import { ProdFirewall } from './ProdFirewall'
import { RouteChrome } from './RouteChrome'
import { IntakeFormPage } from '../organization/onboarding/IntakeForm'
import { OnboardingLayout } from '../orgDashboard/onboarding/OnboardingLayout'
import { SignupRouter } from '../signup/SignupRouter'

const RouteRoot = () => (
  <ProdFirewall>
    <ViewTracker>
      <TrackUtmParams />
      <TrackUserId />
      <TrackAccountStatus />
      <ScrollRestoration />
      <Outlet />
    </ViewTracker>
  </ProdFirewall>
)

const docsRoutes: Array<RouteObject> = [
  {
    path: 'esign-consent',
    element: <DocRoute docHtml={esignAgreement} />,
  },
  {
    path: 'tos-employee',
    element: <FetchDocRoute src="/tos_employee" />,
  },
  {
    path: 'tos-organization',
    element: <FetchDocRoute src="/tos_organization" />,
  },
  {
    path: 'privacy_policy',
    element: <FetchDocRoute src="/privacy_policy" />,
  },
]

const enrollmentRoutes: Array<RouteObject> = [
  { index: true, element: <FindEnrollmentCode /> },
  {
    path: ':enrollmentCode/:referredById?',
    element: <TrackEnrollmentCode />,
    children: [
      {
        index: true,
        element: <EnrollmentLanding />,
      },
      { path: 'register', element: <Register /> },
      { path: 'inactive', element: <InactiveEmployer /> },
    ],
  },
  {
    path: 'summary',
    element: (
      <Authenticate requiredProfile="employee">
        <NewEnrollmentSummary />
      </Authenticate>
    ),
  },
  {
    path: 'reenroll',
    children: [
      {
        index: true,
        element: (
          <Authenticate requiredProfile="employee">
            <ReenrollmentConfirmation />
          </Authenticate>
        ),
      },
    ],
  },
]

const routeConfig: Array<RouteObject> = [
  {
    element: <RouteRoot />,
    children: [
      {
        path: 'onboard',
        element: <OnboardingLayout />,
        children: [
          {
            index: true,
            element: <IntakeFormPage />,
          },
        ],
      },
      {
        element: <RouteChrome type="user" />,
        children: [
          { path: 'login', element: <Login /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
          { path: 'reset-password/:resetToken', element: <ResetPassword /> },
          {
            path: 'logout',
            element: (
              <Authenticate>
                <Logout />
              </Authenticate>
            ),
          },
        ],
      },
      { path: 'signup/:orgInviteCode', element: <SignupRouter /> },
      {
        path: 'enroll',
        element: <EnrollmentRouter />,
        children: [
          {
            path: ':enrollmentCode/:referredById?',
            element: <TrackEnrollmentCode />,
            children: [
              {
                index: true,
                element: <EnrollmentLanding />,
              },
            ],
          },
        ],
      },
      {
        element: <RouteChrome />,
        children: [
          {
            index: true,
            element: (
              <Authenticate>
                <Home />
              </Authenticate>
            ),
          },
          { path: '*', element: <NotFound /> },
          { path: 'tos', element: <CustodialAccountAgreement /> }, // must keep this route stable
          { path: 'docs', children: docsRoutes },
          {
            path: 'enrollment-flow/*', // DML or no-longer-supported route
            element: <Navigate to="/enroll" replace />,
          },
          {
            path: 'enroll',
            element: <EnrollmentRouter />,
            children: enrollmentRoutes,
          },
        ],
      },
      employeeDashboardRoutes,
      orgDashboardRoutes,
    ],
  },
]
const routes: Array<UserWebRouteObject> = routeConfig.map(normalizeRoute)

export const Router = () => {
  return <RouterProvider router={createBrowserRouter(routes)} />
}
